<template>
  <b-card :title="$t('list_pamm_account')">
    <b-row>
      <b-col
        cols="12"
        class=""
      >
        <b-alert
          show
          variant="success"
          class="p-2"
        >
          <h5>
            {{ $t('list_pamm_account_title') }}
          </h5>
        </b-alert>
      </b-col>
      <b-col
        cols="12"
        class="mb-3 text-secondary"
      >
        {{ $t('list_pamm_account_description') }}
      </b-col>
      <b-col
        align-self="center"
        cols="12"
        sm="4"
        class="mb-1"
      >
        <b-input-group
          size="md"
        >
          <b-form-input
            v-model="search"
            name="account_search"
            :placeholder="$t('search')"
            type="text"
            @keyup.enter="searchAccount()"
          />
          <b-input-group-append>
            <b-button
              @click="searchAccount()"
            >
              <feather-icon
                size="14"
                icon="SearchIcon"
              />
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-col>
      <b-col
        v-if="accounts.length > 0"
        cols="12"
      >
        <b-overlay
          :show="accountsShow"
          spinner-variant="primary"
          spinner-type="grow"
          spinner-small
          rounded="sm"
        >
          <b-table
            :items="accounts"
            :fields="accountFields"
            striped
            responsive
            sticky-header="1000px"
          >
            <template #cell(minimum_invest_amount)="data">
              <span>{{ numberFormat(data.item.minimum_invest_amount) }}</span>
            </template>
            <template #cell(aum)="data">
              <span>{{ numberFormat(data.item.aum) }}</span>
            </template>
            <template #cell(risk)="data">
              <b-badge
                :variant="riskVariants[data.item.risk] || 'secondary'"
                pill
              >
                {{ $te(`pamm_risk_${data.item.risk}`) ? $t(`pamm_risk_${data.item.risk}`) : data.item.risk }}
              </b-badge>
            </template>
            <template #cell(followed)="data">
              <b-badge
                :variant="data.item.followed ? 'light-success' : 'light-secondary'"
              >
                {{ data.item.followed ? $t('pamm_following') : $t('pamm_not_following') }}
              </b-badge>
            </template>
            <template #cell(action)="data">
              <b-button
                :href="`${accountDetailsUrl}/${data.item.manager_id}`"
                target="_blank"
                variant="warning"
                class="mx-1"
              >
                {{ $t('details_show') }}
              </b-button>
              <b-button
                :to="{ name: 'pamm_account', params: { id: data.item.manager_id }, query: { name: data.item.nick_name } }"
                variant="success"
                class="mx-1"
              >
                {{ $t('investing') }}
              </b-button>
            </template>
          </b-table>
        </b-overlay>
        <b-row
          v-if="pagination.total > pagination.per_page"
          align-h="center"
          class="mt-2"
        >
          <b-col cols="auto">
            <b-pagination
              v-model="pagination.current_page"
              :total-rows="pagination.total"
              :per-page="pagination.per_page"
              size="sm"
              @input="changePage()"
            />
          </b-col>
        </b-row>
      </b-col>
      <b-col v-if="accounts.length === 0 && !accountsShow">
        <h5 class="text-center py-3 text-danger">
          {{ $t('no_account_to_show') }}
        </h5>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import {
  BOverlay,
  BCard,
  BRow,
  BCol,
  BPagination,
  BTable,
  BInputGroup,
  BFormInput,
  BInputGroupAppend,
  BBadge,
} from 'bootstrap-vue'
import PammApis from '@/modules/pamm/service/apis'
import { numberWithCommas } from '@/utils/index'

const pammApis = new PammApis()

export default {
  components: {
    BOverlay,
    BCard,
    BRow,
    BCol,
    BPagination,
    BTable,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BBadge,
  },
  data() {
    return {
      accountDetailsUrl: process.env.VUE_APP_PAMM_ACCOUNT_BASE_URL,
      show: false,
      accountsShow: false,
      accounts: [],
      search: '',
      riskVariants: {
        low: 'light-success',
        medium: 'light-warning',
        high: 'light-danger',
      },
      pagination: {
        current_page: 1,
        total: 0,
        per_page: 10,
      },
    }
  },
  computed: {
    accountFields() {
      return [
        {
          label: this.$t('pamm_manager_id'),
          key: 'manager_id',
        },
        {
          label: this.$t('account_nickname'),
          key: 'nick_name',
        },
        {
          label: this.$t('minimum_investment'),
          key: 'minimum_invest_amount',
        },
        {
          label: this.$t('pamm_aum'),
          key: 'aum',
        },
        {
          label: this.$t('pamm_investor_count'),
          key: 'investor_count',
        },
        {
          label: this.$t('pamm_risk'),
          key: 'risk',
        },
        {
          label: this.$t('pamm_followed'),
          key: 'followed',
        },
        {
          label: this.$t('action'),
          key: 'action',
        },
      ]
    },
  },
  mounted() {
    this.getAccounts()
  },
  methods: {
    async getAccounts() {
      this.accountsShow = true
      await pammApis.getInvestorManagers({
        page: this.pagination.current_page,
        per_page: this.pagination.per_page,
        search: this.search,
      }).then(res => {
        const { results } = res.data
        if (Array.isArray(results)) {
          this.accounts = results
          return
        }
        this.accounts = results.data ?? []
        if (results.paginate) {
          this.pagination.total = results.paginate.total
          this.pagination.current_page = results.paginate.current_page
          this.pagination.per_page = results.paginate.per_page
        }
      }).catch(err => {
        console.log(err)
      }).finally(() => {
        this.accountsShow = false
      })
    },
    searchAccount() {
      this.pagination.current_page = 1
      this.getAccounts()
    },
    changePage() {
      this.getAccounts()
    },
    numberFormat(value) {
      return value === null || value === undefined ? '-' : numberWithCommas(value)
    },
  },
}
</script>
